import { useCallback, useMemo } from 'react';
import { produce } from 'immer';
import { Text } from '@antimatterhq/ui/components/typography';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Icon } from '@antimatterhq/ui/components/icon';
import {
  useGetObservablesTop10,
  useOptimisticUpdateObservablesTop10,
} from '@/hooks/dbui/useGetObservablesTop10';
import { useGetWorkspaceConfig } from '@/hooks/workspace/useGetWorkspaceConfig';
import { ObservablesTop10 } from '@/components/observables/top10/ObservablesTop10';
import { TableOfContentsProvider } from '@/contexts/TableOfContentContext';
import { TocWrapper } from '@/components/tableOfContents/ToCWrapper';
import { TableOfContents } from '@/components/tableOfContents/TableOfContents';
import { TwoColumnLayout } from '@/components/container';
import { BaseObservablesTable } from '@/components/observables/table/BaseObservablesTable';

export default function ObservablesList() {
  const { data: config } = useGetWorkspaceConfig();
  const { data: top10, isPending: top10IsPending } = useGetObservablesTop10();
  const optimisticUpdate = useOptimisticUpdateObservablesTop10();

  const observables = useMemo(() => {
    return config?.spec?.observables?.kinds?.map(({ name }) => {
      const items = !top10IsPending
        ? (top10?.items?.find((item) => item.kind === name)?.top10 ?? [])
        : undefined;
      return {
        name,
        items,
      };
    });
  }, [config, top10, top10IsPending]);

  const onModifyRiskScore = useCallback(
    (kind: string, value: string, riskScore: number) => {
      const result = produce(top10, (draft) => {
        const item = draft?.items?.find((row) => row.kind === kind);
        if (item) {
          item.top10 = item.top10?.map((row) => {
            if (row.value === value) {
              return {
                ...row,
                riskScore,
              };
            }
            return row;
          });
        }
      });
      if (!result) {
        throw new Error('Failed to update risk score');
      }
      optimisticUpdate(result);
    },
    [optimisticUpdate, top10],
  );

  if (!observables) {
    return (
      <div className="flex flex-col items-center justify-center">
        <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center">
          <Icon
            icon="lucide:list"
            className="h-16 w-16 text-gray-300 dark:text-grey-300"
            data-testid={'no-observables-icon'}
          />
          <Spacer size="base" />
          <Text color="info" data-testid={'no-observables-message'}>
            No obervables found
          </Text>
        </div>
      </div>
    );
  }

  return (
    <TableOfContentsProvider>
      <div>
        <TwoColumnLayout.Root>
          <TwoColumnLayout.LeftColumn>
            <div className="grid grid-cols-2 gap-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2">
              {observables?.map(({ name, items }) => (
                <TocWrapper
                  name={name || ''}
                  key={name}
                  displayName={name || ''}
                >
                  <ObservablesTop10
                    key={name}
                    name={name || ''}
                    items={items}
                    onModifyRiskScore={({ riskScore, value }) => {
                      onModifyRiskScore(name || '', value, riskScore);
                    }}
                  />
                </TocWrapper>
              ))}
            </div>
          </TwoColumnLayout.LeftColumn>
          <TwoColumnLayout.RightColumn>
            <TableOfContents />
          </TwoColumnLayout.RightColumn>
        </TwoColumnLayout.Root>
      </div>
    </TableOfContentsProvider>
  );
}

export const ObservablesListSuspenseFallback = () => {
  return (
    <div>
      <TwoColumnLayout.Root>
        <TwoColumnLayout.LeftColumn>
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2">
            {Array.from({ length: 10 }).map((_, index) => (
              <BaseObservablesTable name="" key={index} />
            ))}
          </div>
        </TwoColumnLayout.LeftColumn>
        <TwoColumnLayout.RightColumn>
          <nav className="sticky top-[20px] overflow-y-auto">
            <Text size="lg" className="mb-4 font-bold">
              Table of contents
            </Text>
            <ul className="list-none p-0"></ul>
          </nav>
        </TwoColumnLayout.RightColumn>
      </TwoColumnLayout.Root>
    </div>
  );
};
